import { motion } from "framer-motion";
import { useLanguage } from "@/i18n";

// Background image for Legal page (sea image)
const LEGAL_BG = "https://customer-assets.emergentagent.com/job_5258f97a-514c-45b1-a984-43e415ab135d/artifacts/wnd8i1u1_fons%20musica.jpeg";

const LegalPage = () => {
  const { t } = useLanguage();
  
  const contactEmail = "spurnacreativa@gmail.com";

  return (
    <div className="min-h-screen relative">
      {/* Background (absolute so the global footer remains visible below) */}
      <div className="absolute inset-0 z-0">
        <img
          src={LEGAL_BG}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 lg:pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide text-foreground mb-6">
              {t.legal.pageTitle}
            </h1>
            <div className="w-16 h-px bg-primary/40" />
          </motion.div>

          {/* Data Protection Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-serif text-2xl lg:text-3xl text-foreground mb-6">
              {t.legal.dataProtectionTitle}
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              {t.legal.dataProtectionIntro}
            </p>
            <p className="text-foreground/80">
              {t.legal.questionsEmail}{" "}
              <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                {contactEmail}
              </a>
            </p>
          </motion.section>

          {/* What We Collect */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h3 className="font-serif text-xl lg:text-2xl text-foreground mb-4">
              {t.legal.whatWeCollectTitle}
            </h3>
            <p className="text-foreground/80 leading-relaxed">
              {t.legal.whatWeCollectText}
            </p>
          </motion.section>

          {/* Cookies */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h3 className="font-serif text-xl lg:text-2xl text-foreground mb-4">
              {t.legal.cookiesTitle}
            </h3>
            <p className="text-foreground/80 leading-relaxed">
              {t.legal.cookiesText}
            </p>
          </motion.section>

          {/* Your Rights */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h3 className="font-serif text-xl lg:text-2xl text-foreground mb-4">
              {t.legal.yourRightsTitle}
            </h3>
            <p className="text-foreground/80 mb-4">{t.legal.yourRightsIntro}</p>
            <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
              {t.legal.rights.map((right, index) => (
                <li key={index}>{right}</li>
              ))}
            </ul>
            <p className="text-foreground/80 mt-4">
              {t.legal.exerciseRights}{" "}
              <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                {contactEmail}
              </a>
            </p>
          </motion.section>

          {/* Divider */}
          <div className="w-full h-px bg-border/30 my-16" />

          {/* Terms of Service */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-serif text-2xl lg:text-3xl text-foreground mb-8">
              {t.legal.tosTitle}
            </h2>

            <div className="space-y-8">
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.tos1Title}</h4>
                <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{t.legal.tos1Text}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.tos2Title}</h4>
                <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{t.legal.tos2Text}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.tos3Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.tos3Text}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.tos4Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.tos4Text}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.tos5Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.tos5Text}</p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.tos6Title}</h4>
                <p className="text-foreground/80">
                  {t.legal.tos6Text}{" "}
                  <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                    {contactEmail}
                  </a>
                </p>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.tos7Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.tos7Text}</p>
              </div>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="w-full h-px bg-border/30 my-16" />

          {/* Privacy Policy */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-serif text-2xl lg:text-3xl text-foreground mb-4">
              {t.legal.privacyTitle}
            </h2>
            <p className="text-foreground/60 text-sm mb-8">{t.legal.privacyLastUpdate}</p>

            <div className="space-y-8">
              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy1Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.privacy1Text}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy2Title}</h4>
                <p className="text-foreground/80 leading-relaxed mb-4">{t.legal.privacy2Text}</p>
                <p className="text-foreground/80 font-medium mb-2">{t.legal.privacy2TechData}</p>
                <ul className="list-disc list-inside text-foreground/80 space-y-1 ml-4">
                  {t.legal.privacy2TechItems.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy3Title}</h4>
                <p className="text-foreground/80 leading-relaxed mb-2">{t.legal.privacy3Text}</p>
                <ul className="list-disc list-inside text-foreground/80 space-y-1 ml-4">
                  {t.legal.privacy3Items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy4Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.privacy4Text}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy5Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.privacy5Text}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy6Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.privacy6Text}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy7Title}</h4>
                <p className="text-foreground/80 leading-relaxed mb-2">{t.legal.privacy7Text}</p>
                <ul className="list-disc list-inside text-foreground/80 space-y-1 ml-4 mb-4">
                  {t.legal.privacy7Items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-foreground/80">
                  {t.legal.privacy7Exercise}{" "}
                  <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                    {contactEmail}
                  </a>
                </p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy8Title}</h4>
                <p className="text-foreground/80 leading-relaxed">{t.legal.privacy8Text}</p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-2">{t.legal.privacy9Title}</h4>
                <p className="text-foreground/80 mb-2">
                  {t.legal.privacy9Text}{" "}
                  <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
                    {contactEmail}
                  </a>
                </p>
                <p className="text-foreground/80 mt-4">{t.legal.dataControllerText}</p>
              </div>
            </div>
          </motion.section>

        </div>
      </div>
    </div>
  );
};

export default LegalPage;
